import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'

import type { ParsedReport } from '@/service/research.api'
import type { ResearchJobEvent } from '@/types/generated/research'

export type ResearchJobKind = 'question' | 'widget-explain' | 'followup'
export type ResearchJobPhase =
  | 'STARTING'
  | 'PENDING'
  | 'RUNNING'
  | 'DELIVERED'
  | 'FAILED'
  | 'KILLED'
  | 'CANCELLED'

export interface ResearchJob {
  id: string
  jobId: string | null
  clientRequestId: string
  threadId: string
  kind: ResearchJobKind
  parentJobId?: string
  rootJobId?: string
  followupMode?: 'simple' | 'report'
  question: string
  assistantMessageId: string
  phase: ResearchJobPhase
  events: ResearchJobEvent[]
  report: ParsedReport | null
  error: string | null
  startedAt: number
  updatedAt: number
  completedAt: number | null
  notifiedAt: number | null
  viewedAt?: number | null
  // Set optimistically the moment the FE stop button fires; cleared once the poller observes
  // a terminal phase, or when the cancel request itself came back as not_cancellable.
  cancelRequestedAt?: number | null
  // User-facing text for a cancel attempt that came back 409 not_cancellable_here, or that
  // threw outright — an honest "couldn't stop this run", never silently ignored.
  cancelError?: string | null
  // Rebuilt from a server thread's history: its real phase is unknown until the first poll, so
  // the notifier treats it as already notified and the poller re-arms that if it is still running.
  restored?: boolean
}

export interface ResearchNotificationEntry {
  id: string
  source: 'research'
  category: 'research'
  severity: 'success' | 'error'
  title: string
  message: string
  timestamp: number
  jobId: string
  threadId: string
  readAt: number | null
  dismissedAt: number | null
}

export const researchJobsAtom = atom<Record<string, ResearchJob>>({})
export const researchNotificationsAtom = atom<ResearchNotificationEntry[]>([])
export const markResearchThreadViewedAtom = atom(null, (get, set, threadId: string) => {
  const now = Date.now()
  set(
    researchJobsAtom,
    Object.fromEntries(
      Object.entries(get(researchJobsAtom)).map(([id, job]) => [
        id,
        job.threadId === threadId && !job.viewedAt ? { ...job, viewedAt: now } : job,
      ])
    )
  )
  set(
    researchNotificationsAtom,
    get(researchNotificationsAtom).map((entry) =>
      entry.threadId === threadId && !entry.readAt ? { ...entry, readAt: now } : entry
    )
  )
})
export const clearResearchRuntimeAtom = atom(null, (_get, set) => {
  set(researchJobsAtom, {})
  set(researchNotificationsAtom, [])
})

export function isResearchJobActive(job: ResearchJob) {
  return job.phase === 'STARTING' || job.phase === 'PENDING' || job.phase === 'RUNNING'
}

export const activeResearchJobsAtom = atom((get) =>
  Object.values(get(researchJobsAtom)).filter(isResearchJobActive)
)

export const activeResearchJobAtomFamily = atomFamily((threadId: string) =>
  atom((get) =>
    Object.values(get(researchJobsAtom)).find(
      (job) => job.threadId === threadId && isResearchJobActive(job)
    )
  )
)

export const deliveredResearchInThreadAtomFamily = atomFamily((threadId: string) =>
  atom((get) =>
    Object.values(get(researchJobsAtom)).some(
      (job) => job.threadId === threadId && job.phase === 'DELIVERED'
    )
  )
)

export const researchJobByBackendIdAtomFamily = atomFamily((jobId: string) =>
  atom((get) => Object.values(get(researchJobsAtom)).find((job) => job.jobId === jobId))
)
